/**
 * WelcomePage - FinList Landing Page
 * "Everything about your money — finally connected."
 * 
 * A comprehensive onboarding and marketing page for new users.
 * Designed with ClickUp-style polish, FinList-native branding.
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import Button from '@/components/ui/Button';
import { 
  Wallet, 
  ArrowRight, 
  Play, 
  ShoppingCart, 
  CheckSquare, 
  Target, 
  BarChart3, 
  PlusCircle,
  Mic,
  Users,
  Bell,
  TrendingUp,
  Shield,
  Globe,
  Sparkles,
  ChevronRight,
  HelpCircle,
  BookOpen
} from 'lucide-react';

// ═══════════════════════════════════════════════════════════════════
// ANIMATION VARIANTS - Premium, subtle, respects reduced-motion
// ═══════════════════════════════════════════════════════════════════

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.2 }
  }
};

const fadeInScale = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.5 } }
};

// Premium hover animation for cards
const cardHover = {
  rest: { y: 0, boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' },
  hover: { 
    y: -4, 
    boxShadow: '0 20px 25px -5px rgba(99, 102, 241, 0.15), 0 10px 10px -5px rgba(99, 102, 241, 0.08)',
    transition: { duration: 0.3, ease: 'easeOut' }
  }
};

// Subtle float animation for visual elements
const subtleFloat = {
  initial: { y: 0 },
  animate: {
    y: [-2, 2, -2],
    transition: { duration: 4, repeat: Infinity, ease: 'easeInOut' }
  }
};

// Gentle pulse for notification elements
const gentlePulse = {
  initial: { scale: 1, opacity: 0.8 },
  animate: {
    scale: [1, 1.05, 1],
    opacity: [0.8, 1, 0.8],
    transition: { duration: 2, repeat: Infinity, ease: 'easeInOut' }
  }
};

// FinList Logo Component (using the actual logo image)
function FinListLogo({ className = "w-12 h-12" }) {
  return (
    <img 
      src="/images/welcome/finlist-logo.png" 
      alt="FinList Logo" 
      className={`${className} rounded-xl`}
    />
  );
}

// ═══════════════════════════════════════════════════════════════════
// ENTERPRISE-GRADE ANIMATED VISUAL COMPONENTS
// Product-inspired illustrations with premium micro-animations
// ═══════════════════════════════════════════════════════════════════

// Hero Visual: Animated Dashboard Concept - "Clarity & Control"
function HeroVisual() {
  const shouldReduceMotion = useReducedMotion();
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div 
      className="relative w-full h-full min-h-[400px] rounded-2xl overflow-hidden bg-gradient-to-br from-slate-900 via-indigo-950 to-purple-950"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Ambient glow - animated */}
      <motion.div 
        className="absolute top-1/4 left-1/2 -translate-x-1/2 w-96 h-96 bg-indigo-500/20 rounded-full blur-3xl"
        animate={shouldReduceMotion ? {} : { 
          scale: [1, 1.1, 1],
          opacity: [0.2, 0.3, 0.2]
        }}
        transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div 
        className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-purple-500/15 rounded-full blur-3xl"
        animate={shouldReduceMotion ? {} : { 
          scale: [1, 1.15, 1],
          opacity: [0.15, 0.25, 0.15]
        }}
        transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
      />
      
      {/* Floating card elements - with animation */}
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 300" fill="none" xmlns="http://www.w3.org/2000/svg">
        {/* Main dashboard card */}
        <motion.g
          animate={shouldReduceMotion ? {} : { y: isHovered ? -3 : 0 }}
          transition={{ duration: 0.3 }}
        >
          <rect x="80" y="60" width="240" height="160" rx="16" fill="url(#heroCardGrad)" fillOpacity="0.4" stroke="url(#heroBorderGrad)" strokeWidth="1"/>
          
          {/* Balance indicator */}
          <rect x="100" y="80" width="100" height="40" rx="8" fill="#818cf8" fillOpacity="0.3"/>
          <rect x="100" y="85" width="60" height="6" rx="3" fill="#a5b4fc"/>
          <rect x="100" y="97" width="80" height="10" rx="2" fill="#c7d2fe"/>
        </motion.g>
        
        {/* Animated chart bars */}
        <motion.rect 
          x="220" y="100" width="12" height="35" rx="4" fill="#818cf8" fillOpacity="0.6"
          animate={shouldReduceMotion ? {} : { height: [30, 35, 30], y: [105, 100, 105] }}
          transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.rect 
          x="238" y="85" width="12" height="50" rx="4" fill="#a78bfa" fillOpacity="0.7"
          animate={shouldReduceMotion ? {} : { height: [45, 55, 45], y: [90, 80, 90] }}
          transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
        />
        <motion.rect 
          x="256" y="95" width="12" height="40" rx="4" fill="#c4b5fd" fillOpacity="0.5"
          animate={shouldReduceMotion ? {} : { height: [35, 45, 35], y: [100, 90, 100] }}
          transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
        />
        <motion.rect 
          x="274" y="110" width="12" height="25" rx="4" fill="#818cf8" fillOpacity="0.4"
          animate={shouldReduceMotion ? {} : { height: [20, 30, 20], y: [115, 105, 115] }}
          transition={{ duration: 3.2, repeat: Infinity, ease: 'easeInOut', delay: 0.3 }}
        />
        <motion.rect 
          x="292" y="75" width="12" height="60" rx="4" fill="#a78bfa" fillOpacity="0.8"
          animate={shouldReduceMotion ? {} : { height: [55, 65, 55], y: [80, 70, 80] }}
          transition={{ duration: 4.5, repeat: Infinity, ease: 'easeInOut', delay: 0.8 }}
        />
        
        {/* Floating envelope cards - subtle float */}
        <motion.g 
          transform="translate(100, 140)"
          animate={shouldReduceMotion ? {} : { y: [-1, 2, -1] }}
          transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
        >
          <rect width="45" height="30" rx="6" fill="#10b981" fillOpacity="0.4" stroke="#34d399" strokeWidth="0.5"/>
          <rect x="5" y="8" width="25" height="3" rx="1.5" fill="#6ee7b7"/>
          <rect x="5" y="15" width="35" height="6" rx="1" fill="#a7f3d0" fillOpacity="0.6"/>
        </motion.g>
        <motion.g 
          transform="translate(155, 140)"
          animate={shouldReduceMotion ? {} : { y: [1, -2, 1] }}
          transition={{ duration: 4.5, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
        >
          <rect width="45" height="30" rx="6" fill="#f59e0b" fillOpacity="0.4" stroke="#fbbf24" strokeWidth="0.5"/>
          <rect x="5" y="8" width="20" height="3" rx="1.5" fill="#fcd34d"/>
          <rect x="5" y="15" width="30" height="6" rx="1" fill="#fde68a" fillOpacity="0.6"/>
        </motion.g>
        <motion.g 
          transform="translate(210, 140)"
          animate={shouldReduceMotion ? {} : { y: [-1.5, 1.5, -1.5] }}
          transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
        >
          <rect width="45" height="30" rx="6" fill="#8b5cf6" fillOpacity="0.4" stroke="#a78bfa" strokeWidth="0.5"/>
          <rect x="5" y="8" width="30" height="3" rx="1.5" fill="#c4b5fd"/>
          <rect x="5" y="15" width="25" height="6" rx="1" fill="#ddd6fe" fillOpacity="0.6"/>
        </motion.g>
        
        {/* Progress arc */}
        <circle cx="290" cy="190" r="25" fill="none" stroke="#4f46e5" strokeWidth="4" strokeOpacity="0.2"/>
        <motion.circle 
          cx="290" cy="190" r="25" fill="none" stroke="url(#progressGrad)" strokeWidth="4" strokeLinecap="round"
          initial={{ strokeDasharray: '0 157' }}
          animate={shouldReduceMotion ? { strokeDasharray: '110 157' } : { strokeDasharray: ['60 157', '110 157', '60 157'] }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
        />
        
        {/* Animated light particles */}
        <motion.circle 
          cx="50" cy="50" r="2" fill="#818cf8"
          animate={shouldReduceMotion ? {} : { opacity: [0.4, 0.8, 0.4] }}
          transition={{ duration: 3, repeat: Infinity }}
        />
        <motion.circle 
          cx="350" cy="80" r="1.5" fill="#a78bfa"
          animate={shouldReduceMotion ? {} : { opacity: [0.3, 0.7, 0.3] }}
          transition={{ duration: 4, repeat: Infinity, delay: 1 }}
        />
        <motion.circle 
          cx="30" cy="200" r="1" fill="#c4b5fd"
          animate={shouldReduceMotion ? {} : { opacity: [0.2, 0.6, 0.2] }}
          transition={{ duration: 5, repeat: Infinity, delay: 2 }}
        />
        <motion.circle 
          cx="370" cy="250" r="2" fill="#818cf8"
          animate={shouldReduceMotion ? {} : { opacity: [0.2, 0.5, 0.2] }}
          transition={{ duration: 3.5, repeat: Infinity, delay: 0.5 }}
        />
        
        <defs>
          <linearGradient id="heroCardGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#6366f1"/>
            <stop offset="100%" stopColor="#8b5cf6"/>
          </linearGradient>
          <linearGradient id="heroBorderGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#818cf8" stopOpacity="0.5"/>
            <stop offset="100%" stopColor="#a78bfa" stopOpacity="0.2"/>
          </linearGradient>
          <linearGradient id="progressGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#10b981"/>
            <stop offset="100%" stopColor="#6366f1"/>
          </linearGradient>
        </defs>
      </svg>
    </motion.div>
  );
}

// Budget Visual: Abstract Envelope Allocation with animations
function BudgetVisual() {
  const shouldReduceMotion = useReducedMotion();
  
  return (
    <motion.div 
      className="relative w-full h-40 rounded-xl overflow-hidden bg-gradient-to-br from-indigo-950 via-slate-900 to-purple-950"
      whileHover={shouldReduceMotion ? {} : { scale: 1.02 }}
      transition={{ duration: 0.3 }}
    >
      <motion.div 
        className="absolute top-1/2 left-1/4 w-32 h-32 bg-indigo-500/20 rounded-full blur-2xl"
        animate={shouldReduceMotion ? {} : { scale: [1, 1.1, 1], opacity: [0.2, 0.3, 0.2] }}
        transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
      />
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 300 160" fill="none">
        {/* Central balance source - pulsing */}
        <motion.circle 
          cx="150" cy="80" r="20" fill="url(#budgetCenterGrad)" fillOpacity="0.6"
          animate={shouldReduceMotion ? {} : { scale: [1, 1.1, 1] }}
          transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
        />
        <circle cx="150" cy="80" r="25" fill="none" stroke="#818cf8" strokeWidth="1" strokeOpacity="0.3"/>
        
        {/* Envelope containers - staggered stack/settle animation */}
        <motion.g 
          transform="translate(40, 50)"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={shouldReduceMotion ? { duration: 0 } : { duration: 0.6, delay: 0.1 }}
        >
          <motion.g
            animate={shouldReduceMotion ? {} : { y: [-1, 1, -1] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
          >
            <rect width="40" height="55" rx="8" fill="#10b981" fillOpacity="0.25" stroke="#34d399" strokeWidth="0.5"/>
            <rect x="8" y="12" width="24" height="4" rx="2" fill="#6ee7b7" fillOpacity="0.7"/>
            <rect x="8" y="22" width="18" height="3" rx="1.5" fill="#a7f3d0" fillOpacity="0.5"/>
          </motion.g>
        </motion.g>
        <motion.g 
          transform="translate(95, 35)"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={shouldReduceMotion ? { duration: 0 } : { duration: 0.6, delay: 0.2 }}
        >
          <motion.g
            animate={shouldReduceMotion ? {} : { y: [1, -1, 1] }}
            transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut', delay: 0.3 }}
          >
            <rect width="40" height="55" rx="8" fill="#f59e0b" fillOpacity="0.25" stroke="#fbbf24" strokeWidth="0.5"/>
            <rect x="8" y="12" width="20" height="4" rx="2" fill="#fcd34d" fillOpacity="0.7"/>
            <rect x="8" y="22" width="28" height="3" rx="1.5" fill="#fde68a" fillOpacity="0.5"/>
          </motion.g>
        </motion.g>
        <motion.g 
          transform="translate(165, 35)"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={shouldReduceMotion ? { duration: 0 } : { duration: 0.6, delay: 0.3 }}
        >
          <motion.g
            animate={shouldReduceMotion ? {} : { y: [-1.5, 1.5, -1.5] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut', delay: 0.6 }}
          >
            <rect width="40" height="55" rx="8" fill="#8b5cf6" fillOpacity="0.25" stroke="#a78bfa" strokeWidth="0.5"/>
            <rect x="8" y="12" width="26" height="4" rx="2" fill="#c4b5fd" fillOpacity="0.7"/>
            <rect x="8" y="22" width="16" height="3" rx="1.5" fill="#ddd6fe" fillOpacity="0.5"/>
          </motion.g>
        </motion.g>
        <motion.g 
          transform="translate(220, 50)"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={shouldReduceMotion ? { duration: 0 } : { duration: 0.6, delay: 0.4 }}
        >
          <motion.g
            animate={shouldReduceMotion ? {} : { y: [1, -1.5, 1] }}
            transition={{ duration: 3.2, repeat: Infinity, ease: 'easeInOut', delay: 0.9 }}
          >
            <rect width="40" height="55" rx="8" fill="#06b6d4" fillOpacity="0.25" stroke="#22d3ee" strokeWidth="0.5"/>
            <rect x="8" y="12" width="22" height="4" rx="2" fill="#67e8f9" fillOpacity="0.7"/>
            <rect x="8" y="22" width="30" height="3" rx="1.5" fill="#a5f3fc" fillOpacity="0.5"/>
          </motion.g>
        </motion.g>
        
        {/* Connection lines - animated dash */}
        <motion.line 
          x1="130" y1="80" x2="80" y2="77" stroke="#818cf8" strokeWidth="1" strokeOpacity="0.3" strokeDasharray="3 2"
          animate={shouldReduceMotion ? {} : { strokeDashoffset: [0, 10] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
        />
        <motion.line 
          x1="130" y1="80" x2="115" y2="62" stroke="#818cf8" strokeWidth="1" strokeOpacity="0.3" strokeDasharray="3 2"
          animate={shouldReduceMotion ? {} : { strokeDashoffset: [0, 10] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: 'linear' }}
        />
        <motion.line 
          x1="170" y1="80" x2="185" y2="62" stroke="#818cf8" strokeWidth="1" strokeOpacity="0.3" strokeDasharray="3 2"
          animate={shouldReduceMotion ? {} : { strokeDashoffset: [0, -10] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: 'linear' }}
        />
        <motion.line 
          x1="170" y1="80" x2="220" y2="77" stroke="#818cf8" strokeWidth="1" strokeOpacity="0.3" strokeDasharray="3 2"
          animate={shouldReduceMotion ? {} : { strokeDashoffset: [0, -10] }}
          transition={{ duration: 2.1, repeat: Infinity, ease: 'linear' }}
        />
        
        <defs>
          <radialGradient id="budgetCenterGrad">
            <stop offset="0%" stopColor="#a78bfa"/>
            <stop offset="100%" stopColor="#6366f1"/>
          </radialGradient>
        </defs>
      </svg>
    </motion.div>
  );
}

// Transactions Visual: Abstract Money Flow with animations
function TransactionsVisual() {
  const shouldReduceMotion = useReducedMotion();
  
  return (
    <motion.div 
      className="relative w-full h-40 rounded-xl overflow-hidden bg-gradient-to-br from-slate-900 via-emerald-950 to-slate-900"
      whileHover={shouldReduceMotion ? {} : { scale: 1.02 }}
      transition={{ duration: 0.3 }}
    >
      <motion.div 
        className="absolute bottom-0 right-1/4 w-32 h-32 bg-emerald-500/15 rounded-full blur-2xl"
        animate={shouldReduceMotion ? {} : { scale: [1, 1.15, 1], opacity: [0.15, 0.25, 0.15] }}
        transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
      />
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 300 160" fill="none">
        {/* Central hub - pulsing */}
        <motion.circle 
          cx="150" cy="80" r="22" fill="url(#transHubGrad)" fillOpacity="0.5"
          animate={shouldReduceMotion ? {} : { scale: [1, 1.08, 1] }}
          transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
        />
        <circle cx="150" cy="80" r="28" fill="none" stroke="#10b981" strokeWidth="1" strokeOpacity="0.4"/>
        
        {/* Inflow arrow (left) - animated flow */}
        <motion.path 
          d="M30 80 Q70 80 110 80" 
          stroke="#34d399" 
          strokeWidth="2" 
          fill="none"
          strokeDasharray="8 4"
          animate={shouldReduceMotion ? {} : { strokeDashoffset: [48, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
        />
        <motion.circle 
          cx="30" cy="80" r="12" fill="#10b981" fillOpacity="0.3" stroke="#34d399" strokeWidth="0.5"
          animate={shouldReduceMotion ? {} : { scale: [1, 1.1, 1] }}
          transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
        />
        
        {/* Animated flow particles - inflow */}
        <motion.circle
          cx="30" cy="80" r="3" fill="#34d399"
          animate={shouldReduceMotion ? { opacity: 0 } : { cx: [30, 110], opacity: [0.8, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeOut', repeatDelay: 6 }}
        />
        <motion.circle
          cx="30" cy="80" r="2" fill="#6ee7b7"
          animate={shouldReduceMotion ? { opacity: 0 } : { cx: [30, 110], opacity: [0.6, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeOut', delay: 0.3, repeatDelay: 6 }}
        />
        
        {/* Outflow arrow (right) - animated flow */}
        <motion.path 
          d="M190 80 Q230 80 270 80" 
          stroke="#f87171" 
          strokeWidth="2" 
          fill="none"
          strokeDasharray="8 4"
          animate={shouldReduceMotion ? {} : { strokeDashoffset: [0, -48] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
        />
        <motion.circle 
          cx="270" cy="80" r="12" fill="#ef4444" fillOpacity="0.3" stroke="#f87171" strokeWidth="0.5"
          animate={shouldReduceMotion ? {} : { scale: [1, 1.1, 1] }}
          transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
        />
        
        {/* Animated flow particles - outflow */}
        <motion.circle
          cx="190" cy="80" r="3" fill="#f87171"
          animate={shouldReduceMotion ? { opacity: 0 } : { cx: [190, 270], opacity: [0.8, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeOut', delay: 4, repeatDelay: 6 }}
        />
        <motion.circle
          cx="190" cy="80" r="2" fill="#fca5a5"
          animate={shouldReduceMotion ? { opacity: 0 } : { cx: [190, 270], opacity: [0.6, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeOut', delay: 4.3, repeatDelay: 6 }}
        />
        
        {/* Transfer arrows (up/down) */}
        <motion.path 
          d="M150 50 Q150 35 150 20" 
          stroke="#818cf8" 
          strokeWidth="1.5" 
          fill="none"
          strokeDasharray="4 3"
          animate={shouldReduceMotion ? {} : { strokeDashoffset: [0, -14] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
        />
        <motion.path 
          d="M150 110 Q150 125 150 140" 
          stroke="#818cf8" 
          strokeWidth="1.5" 
          fill="none"
          strokeDasharray="4 3"
          animate={shouldReduceMotion ? {} : { strokeDashoffset: [0, 14] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
        />
        
        <defs>
          <radialGradient id="transHubGrad">
            <stop offset="0%" stopColor="#34d399"/>
            <stop offset="100%" stopColor="#10b981"/>
          </radialGradient>
        </defs>
      </svg>
    </motion.div>
  );
}

// Shopping List Visual: Abstract Planning Concept with animations
function ShoppingVisual() {
  const shouldReduceMotion = useReducedMotion();
  
  return (
    <motion.div 
      className="relative w-full h-40 rounded-xl overflow-hidden bg-gradient-to-br from-amber-950/50 via-slate-900 to-orange-950/40"
      whileHover={shouldReduceMotion ? {} : { scale: 1.02 }}
      transition={{ duration: 0.3 }}
    >
      <motion.div 
        className="absolute top-1/3 left-1/3 w-28 h-28 bg-amber-500/15 rounded-full blur-2xl"
        animate={shouldReduceMotion ? {} : { scale: [1, 1.12, 1], opacity: [0.15, 0.22, 0.15] }}
        transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
      />
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 300 160" fill="none">
        {/* List card */}
        <rect x="90" y="25" width="120" height="110" rx="12" fill="#1e293b" fillOpacity="0.8" stroke="#f59e0b" strokeWidth="0.5" strokeOpacity="0.4"/>
        
        {/* List items with staggered checkmark animations */}
        <motion.g 
          transform="translate(105, 45)"
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={shouldReduceMotion ? { duration: 0 } : { duration: 0.4, delay: 0.2 }}
        >
          <rect width="16" height="16" rx="4" fill="#10b981" fillOpacity="0.3" stroke="#34d399" strokeWidth="0.5"/>
          <motion.path 
            d="M4 8 L7 11 L12 5" 
            stroke="#34d399" 
            strokeWidth="1.5" 
            strokeLinecap="round" 
            strokeLinejoin="round"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 1 }}
            transition={shouldReduceMotion ? { duration: 0 } : { duration: 0.5, delay: 0.5 }}
          />
          <rect x="24" y="4" width="60" height="6" rx="2" fill="#94a3b8" fillOpacity="0.4"/>
        </motion.g>
        <motion.g 
          transform="translate(105, 70)"
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={shouldReduceMotion ? { duration: 0 } : { duration: 0.4, delay: 0.4 }}
        >
          <rect width="16" height="16" rx="4" fill="#10b981" fillOpacity="0.3" stroke="#34d399" strokeWidth="0.5"/>
          <motion.path 
            d="M4 8 L7 11 L12 5" 
            stroke="#34d399" 
            strokeWidth="1.5" 
            strokeLinecap="round" 
            strokeLinejoin="round"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 1 }}
            transition={shouldReduceMotion ? { duration: 0 } : { duration: 0.5, delay: 0.8 }}
          />
          <rect x="24" y="4" width="50" height="6" rx="2" fill="#94a3b8" fillOpacity="0.4"/>
        </motion.g>
        <motion.g 
          transform="translate(105, 95)"
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={shouldReduceMotion ? { duration: 0 } : { duration: 0.4, delay: 0.6 }}
        >
          <motion.rect 
            width="16" height="16" rx="4" fill="#f59e0b" fillOpacity="0.2" stroke="#fbbf24" strokeWidth="0.5"
            animate={shouldReduceMotion ? {} : { strokeOpacity: [0.5, 0.8, 0.5] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          />
          <rect x="24" y="4" width="70" height="6" rx="2" fill="#fcd34d" fillOpacity="0.3"/>
        </motion.g>
        
        {/* Microphone icon with pulse animation */}
        <motion.g transform="translate(230, 55)">
          <motion.rect 
            x="8" y="0" width="14" height="24" rx="7" fill="#f59e0b" fillOpacity="0.3" stroke="#fbbf24" strokeWidth="0.5"
            animate={shouldReduceMotion ? {} : { fillOpacity: [0.3, 0.5, 0.3] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
          />
          <path d="M8 18 Q8 28 15 28 Q22 28 22 18" stroke="#fbbf24" strokeWidth="1" fill="none"/>
          <line x1="15" y1="28" x2="15" y2="35" stroke="#fbbf24" strokeWidth="1"/>
          <line x1="10" y1="35" x2="20" y2="35" stroke="#fbbf24" strokeWidth="1"/>
          {/* Sound waves */}
          <motion.path 
            d="M26 8 Q30 12 26 16" 
            stroke="#fbbf24" 
            strokeWidth="1" 
            fill="none" 
            strokeOpacity="0.6"
            animate={shouldReduceMotion ? {} : { opacity: [0, 0.6, 0], x: [0, 2, 0] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.path 
            d="M30 5 Q36 12 30 19" 
            stroke="#fbbf24" 
            strokeWidth="1" 
            fill="none" 
            strokeOpacity="0.4"
            animate={shouldReduceMotion ? {} : { opacity: [0, 0.4, 0], x: [0, 3, 0] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut', delay: 0.2 }}
          />
        </motion.g>
      </svg>
    </motion.div>
  );
}

// FinTasks Visual: Abstract Task Cards with animations
function TasksVisual() {
  const shouldReduceMotion = useReducedMotion();
  
  return (
    <motion.div 
      className="relative w-full h-40 rounded-xl overflow-hidden bg-gradient-to-br from-blue-950/50 via-slate-900 to-indigo-950/40"
      whileHover={shouldReduceMotion ? {} : { scale: 1.02 }}
      transition={{ duration: 0.3 }}
    >
      <motion.div 
        className="absolute bottom-1/4 left-1/4 w-32 h-32 bg-blue-500/15 rounded-full blur-2xl"
        animate={shouldReduceMotion ? {} : { scale: [1, 1.1, 1], opacity: [0.15, 0.22, 0.15] }}
        transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
      />
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 300 160" fill="none">
        {/* Stacked task cards - staggered reveal animation */}
        <motion.rect 
          x="60" y="50" width="180" height="45" rx="8" fill="#1e293b" fillOpacity="0.6" stroke="#3b82f6" strokeWidth="0.5" strokeOpacity="0.3"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={shouldReduceMotion ? { duration: 0 } : { duration: 0.4, delay: 0.3 }}
        />
        <motion.rect 
          x="70" y="40" width="180" height="45" rx="8" fill="#1e293b" fillOpacity="0.7" stroke="#3b82f6" strokeWidth="0.5" strokeOpacity="0.4"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={shouldReduceMotion ? { duration: 0 } : { duration: 0.4, delay: 0.15 }}
        />
        <motion.g
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={shouldReduceMotion ? { duration: 0 } : { duration: 0.4, delay: 0 }}
        >
          <motion.rect 
            x="80" y="30" width="180" height="45" rx="8" fill="#1e293b" fillOpacity="0.9" stroke="#3b82f6" strokeWidth="0.5" strokeOpacity="0.6"
            animate={shouldReduceMotion ? {} : { y: [30, 28, 30] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
          />
        </motion.g>
        
        {/* Top card content */}
        <motion.g 
          transform="translate(95, 42)"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={shouldReduceMotion ? { duration: 0 } : { duration: 0.3, delay: 0.5 }}
        >
          <rect width="14" height="14" rx="3" fill="#3b82f6" fillOpacity="0.3" stroke="#60a5fa" strokeWidth="0.5"/>
          <motion.path 
            d="M3 7 L6 10 L11 4" 
            stroke="#60a5fa" 
            strokeWidth="1.5" 
            strokeLinecap="round" 
            strokeLinejoin="round"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={shouldReduceMotion ? { duration: 0 } : { duration: 0.5, delay: 0.7 }}
          />
          <rect x="22" y="3" width="70" height="5" rx="2" fill="#93c5fd" fillOpacity="0.5"/>
        </motion.g>
        
        {/* Progress indicators - animated fill */}
        <rect x="95" y="100" width="130" height="6" rx="3" fill="#1e293b"/>
        <motion.rect 
          x="95" y="100" rx="3" fill="url(#taskProgressGrad)"
          initial={{ width: 0 }}
          animate={{ width: [0, 90, 70, 90] }}
          transition={shouldReduceMotion ? { duration: 0, width: 90 } : { duration: 4, repeat: Infinity, ease: 'easeInOut', repeatDelay: 2 }}
          height="6"
        />
        
        {/* Notification dot - pulsing */}
        <motion.circle 
          cx="245" cy="35" r="6" fill="#f59e0b" fillOpacity="0.8"
          animate={shouldReduceMotion ? {} : { scale: [1, 1.2, 1] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.circle 
          cx="245" cy="35" r="8" fill="none" stroke="#fbbf24" strokeWidth="0.5" strokeOpacity="0.5"
          animate={shouldReduceMotion ? {} : { scale: [1, 1.3, 1], opacity: [0.5, 0.2, 0.5] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
        />
        
        <defs>
          <linearGradient id="taskProgressGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#3b82f6"/>
            <stop offset="100%" stopColor="#8b5cf6"/>
          </linearGradient>
        </defs>
      </svg>
    </motion.div>
  );
}

// Goals Visual: Abstract Progress & Growth with animations
function GoalsVisual() {
  const shouldReduceMotion = useReducedMotion();
  
  return (
    <motion.div 
      className="relative w-full h-40 rounded-xl overflow-hidden bg-gradient-to-br from-purple-950/50 via-slate-900 to-violet-950/40"
      whileHover={shouldReduceMotion ? {} : { scale: 1.02 }}
      transition={{ duration: 0.3 }}
    >
      <motion.div 
        className="absolute top-1/3 right-1/4 w-28 h-28 bg-purple-500/15 rounded-full blur-2xl"
        animate={shouldReduceMotion ? {} : { scale: [1, 1.15, 1], opacity: [0.15, 0.25, 0.15] }}
        transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
      />
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 300 160" fill="none">
        {/* Rising path/milestones - animated draw */}
        <motion.path 
          d="M40 130 Q80 110 120 90 Q160 70 200 50 Q240 30 260 25" 
          stroke="url(#goalPathGrad)" 
          strokeWidth="3" 
          fill="none" 
          strokeLinecap="round"
          initial={{ pathLength: 0, opacity: 0.5 }}
          animate={{ pathLength: 1, opacity: 1 }}
          transition={shouldReduceMotion ? { duration: 0, pathLength: 1 } : { duration: 2, delay: 0.3 }}
        />
        
        {/* Milestone markers - staggered reveal with pulse */}
        <motion.circle 
          cx="40" cy="130" r="6" fill="#8b5cf6" fillOpacity="0.4" stroke="#a78bfa" strokeWidth="1"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={shouldReduceMotion ? { duration: 0 } : { duration: 0.3, delay: 0.5 }}
        />
        <motion.circle 
          cx="120" cy="90" r="8" fill="#8b5cf6" fillOpacity="0.5" stroke="#a78bfa" strokeWidth="1"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={shouldReduceMotion ? { duration: 0 } : { duration: 0.3, delay: 1 }}
        />
        <motion.circle 
          cx="200" cy="50" r="10" fill="#8b5cf6" fillOpacity="0.6" stroke="#a78bfa" strokeWidth="1"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={shouldReduceMotion ? { duration: 0 } : { duration: 0.3, delay: 1.5 }}
        />
        <motion.circle 
          cx="260" cy="25" r="12" fill="url(#goalEndGrad)" stroke="#c4b5fd" strokeWidth="1.5"
          initial={{ scale: 0 }}
          animate={{ scale: [0, 1, 1.1, 1] }}
          transition={shouldReduceMotion ? { duration: 0, scale: 1 } : { duration: 0.6, delay: 2 }}
        />
        
        {/* Achievement glow at end - pulsing */}
        <motion.circle 
          cx="260" cy="25" r="18" fill="none" stroke="#a78bfa" strokeWidth="0.5" strokeOpacity="0.4"
          animate={shouldReduceMotion ? {} : { scale: [1, 1.2, 1], opacity: [0.4, 0.2, 0.4] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut', delay: 2.5 }}
        />
        <motion.circle 
          cx="260" cy="25" r="24" fill="none" stroke="#a78bfa" strokeWidth="0.3" strokeOpacity="0.2"
          animate={shouldReduceMotion ? {} : { scale: [1, 1.3, 1], opacity: [0.2, 0.1, 0.2] }}
          transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut', delay: 2.7 }}
        />
        
        {/* Progress bars on the side - animated fill loop */}
        <g transform="translate(30, 30)">
          <rect width="8" height="50" rx="4" fill="#1e293b"/>
          <motion.rect 
            y="20" width="8" rx="4" fill="#10b981" fillOpacity="0.7"
            animate={shouldReduceMotion ? { height: 30 } : { height: [0, 30, 30, 0], y: [50, 20, 20, 50] }}
            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut', times: [0, 0.4, 0.8, 1] }}
          />
        </g>
        <g transform="translate(45, 40)">
          <rect width="8" height="40" rx="4" fill="#1e293b"/>
          <motion.rect 
            y="10" width="8" rx="4" fill="#8b5cf6" fillOpacity="0.7"
            animate={shouldReduceMotion ? { height: 30 } : { height: [0, 30, 30, 0], y: [40, 10, 10, 40] }}
            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut', times: [0, 0.4, 0.8, 1], delay: 0.5 }}
          />
        </g>
        
        <defs>
          <linearGradient id="goalPathGrad" x1="0%" y1="100%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#6366f1" stopOpacity="0.5"/>
            <stop offset="100%" stopColor="#a78bfa"/>
          </linearGradient>
          <radialGradient id="goalEndGrad">
            <stop offset="0%" stopColor="#c4b5fd"/>
            <stop offset="100%" stopColor="#8b5cf6"/>
          </radialGradient>
        </defs>
      </svg>
    </motion.div>
  );
}

// Help Visual: Abstract Support & Resources with animations
function HelpVisual() {
  const shouldReduceMotion = useReducedMotion();
  
  return (
    <motion.div 
      className="relative w-full h-40 rounded-xl overflow-hidden bg-gradient-to-br from-cyan-950/50 via-slate-900 to-teal-950/40"
      whileHover={shouldReduceMotion ? {} : { scale: 1.02 }}
      transition={{ duration: 0.3 }}
    >
      <motion.div 
        className="absolute top-1/4 right-1/3 w-28 h-28 bg-cyan-500/15 rounded-full blur-2xl"
        animate={shouldReduceMotion ? {} : { scale: [1, 1.12, 1], opacity: [0.15, 0.25, 0.15] }}
        transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
      />
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 300 160" fill="none">
        {/* Central help icon */}
        <motion.g
          animate={shouldReduceMotion ? {} : { y: [-2, 2, -2] }}
          transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
        >
          <circle cx="150" cy="70" r="35" fill="#0891b2" fillOpacity="0.2" stroke="#22d3ee" strokeWidth="1"/>
          <motion.circle 
            cx="150" cy="70" r="42" fill="none" stroke="#22d3ee" strokeWidth="0.5" strokeOpacity="0.4"
            animate={shouldReduceMotion ? {} : { scale: [1, 1.1, 1], opacity: [0.4, 0.2, 0.4] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
          />
          {/* Question mark */}
          <motion.text 
            x="150" y="80" textAnchor="middle" fill="#67e8f9" fontSize="36" fontFamily="system-ui" fontWeight="bold"
            animate={shouldReduceMotion ? {} : { scale: [1, 1.05, 1] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          >
            ?
          </motion.text>
        </motion.g>
        
        {/* Floating info cards */}
        <motion.g 
          transform="translate(40, 35)"
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={shouldReduceMotion ? { duration: 0 } : { duration: 0.4, delay: 0.2 }}
        >
          <motion.g
            animate={shouldReduceMotion ? {} : { y: [-1, 1, -1] }}
            transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut' }}
          >
            <rect width="50" height="35" rx="6" fill="#1e293b" fillOpacity="0.8" stroke="#22d3ee" strokeWidth="0.5"/>
            <rect x="8" y="10" width="20" height="3" rx="1.5" fill="#67e8f9" fillOpacity="0.6"/>
            <rect x="8" y="18" width="34" height="3" rx="1.5" fill="#a5f3fc" fillOpacity="0.4"/>
            <rect x="8" y="24" width="28" height="3" rx="1.5" fill="#a5f3fc" fillOpacity="0.3"/>
          </motion.g>
        </motion.g>
        
        <motion.g 
          transform="translate(210, 30)"
          initial={{ opacity: 0, x: 10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={shouldReduceMotion ? { duration: 0 } : { duration: 0.4, delay: 0.4 }}
        >
          <motion.g
            animate={shouldReduceMotion ? {} : { y: [1, -1, 1] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
          >
            <rect width="50" height="35" rx="6" fill="#1e293b" fillOpacity="0.8" stroke="#22d3ee" strokeWidth="0.5"/>
            <rect x="8" y="10" width="30" height="3" rx="1.5" fill="#67e8f9" fillOpacity="0.6"/>
            <rect x="8" y="18" width="25" height="3" rx="1.5" fill="#a5f3fc" fillOpacity="0.4"/>
            <rect x="8" y="24" width="35" height="3" rx="1.5" fill="#a5f3fc" fillOpacity="0.3"/>
          </motion.g>
        </motion.g>
        
        {/* Connecting dots - animated */}
        <motion.circle 
          cx="100" cy="70" r="3" fill="#22d3ee"
          animate={shouldReduceMotion ? {} : { opacity: [0.3, 0.8, 0.3] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.circle 
          cx="200" cy="70" r="3" fill="#22d3ee"
          animate={shouldReduceMotion ? {} : { opacity: [0.3, 0.8, 0.3] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
        />
        
        {/* Bottom resources bar */}
        <motion.g 
          transform="translate(60, 115)"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={shouldReduceMotion ? { duration: 0 } : { duration: 0.4, delay: 0.6 }}
        >
          <rect width="180" height="30" rx="8" fill="#1e293b" fillOpacity="0.6" stroke="#0891b2" strokeWidth="0.5" strokeOpacity="0.4"/>
          {/* Icons */}
          <circle cx="30" cy="15" r="8" fill="#0891b2" fillOpacity="0.3"/>
          <circle cx="70" cy="15" r="8" fill="#0891b2" fillOpacity="0.3"/>
          <circle cx="110" cy="15" r="8" fill="#0891b2" fillOpacity="0.3"/>
          <circle cx="150" cy="15" r="8" fill="#0891b2" fillOpacity="0.3"/>
        </motion.g>
      </svg>
    </motion.div>
  );
}

// Dashboard Overview Visual
function DashboardVisual() {
  return (
    <div className="relative w-full h-full min-h-[350px] rounded-2xl overflow-hidden bg-gradient-to-br from-slate-900 via-indigo-950 to-purple-950">
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-64 h-64 bg-indigo-500/15 rounded-full blur-3xl" />
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 350" fill="none">
        {/* Main metrics row */}
        <g transform="translate(40, 40)">
          <rect width="100" height="70" rx="10" fill="#1e293b" fillOpacity="0.8" stroke="#10b981" strokeWidth="0.5" strokeOpacity="0.5"/>
          <rect x="12" y="15" width="50" height="6" rx="3" fill="#6ee7b7" fillOpacity="0.6"/>
          <rect x="12" y="30" width="70" height="12" rx="2" fill="#34d399" fillOpacity="0.4"/>
          <text x="12" y="56" fill="#a7f3d0" fontSize="8" fontFamily="system-ui">Safe to Spend</text>
        </g>
        <g transform="translate(150, 40)">
          <rect width="100" height="70" rx="10" fill="#1e293b" fillOpacity="0.8" stroke="#818cf8" strokeWidth="0.5" strokeOpacity="0.5"/>
          <rect x="12" y="15" width="40" height="6" rx="3" fill="#a5b4fc" fillOpacity="0.6"/>
          <rect x="12" y="30" width="60" height="12" rx="2" fill="#818cf8" fillOpacity="0.4"/>
          <text x="12" y="56" fill="#c7d2fe" fontSize="8" fontFamily="system-ui">Net Balance</text>
        </g>
        <g transform="translate(260, 40)">
          <rect width="100" height="70" rx="10" fill="#1e293b" fillOpacity="0.8" stroke="#f59e0b" strokeWidth="0.5" strokeOpacity="0.5"/>
          <rect x="12" y="15" width="55" height="6" rx="3" fill="#fcd34d" fillOpacity="0.6"/>
          <rect x="12" y="30" width="50" height="12" rx="2" fill="#f59e0b" fillOpacity="0.4"/>
          <text x="12" y="56" fill="#fde68a" fontSize="8" fontFamily="system-ui">This Month</text>
        </g>
        
        {/* Chart area */}
        <rect x="40" y="130" width="320" height="140" rx="12" fill="#1e293b" fillOpacity="0.6" stroke="#4f46e5" strokeWidth="0.5" strokeOpacity="0.3"/>
        
        {/* Bar chart */}
        <rect x="70" y="200" width="20" height="50" rx="4" fill="#818cf8" fillOpacity="0.6"/>
        <rect x="110" y="180" width="20" height="70" rx="4" fill="#a78bfa" fillOpacity="0.7"/>
        <rect x="150" y="190" width="20" height="60" rx="4" fill="#c4b5fd" fillOpacity="0.5"/>
        <rect x="190" y="170" width="20" height="80" rx="4" fill="#818cf8" fillOpacity="0.8"/>
        <rect x="230" y="210" width="20" height="40" rx="4" fill="#a78bfa" fillOpacity="0.5"/>
        <rect x="270" y="185" width="20" height="65" rx="4" fill="#c4b5fd" fillOpacity="0.6"/>
        <rect x="310" y="195" width="20" height="55" rx="4" fill="#818cf8" fillOpacity="0.7"/>
        
        {/* Trend line */}
        <path d="M70 200 Q150 170 190 175 Q250 185 330 190" stroke="url(#dashTrendGrad)" strokeWidth="2" fill="none" strokeLinecap="round"/>
        
        {/* Bottom cards */}
        <g transform="translate(40, 290)">
          <rect width="150" height="45" rx="8" fill="#1e293b" fillOpacity="0.7" stroke="#10b981" strokeWidth="0.5" strokeOpacity="0.4"/>
          <rect x="10" y="12" width="80" height="5" rx="2" fill="#6ee7b7" fillOpacity="0.5"/>
          <rect x="10" y="24" width="60" height="4" rx="2" fill="#a7f3d0" fillOpacity="0.3"/>
        </g>
        <g transform="translate(210, 290)">
          <rect width="150" height="45" rx="8" fill="#1e293b" fillOpacity="0.7" stroke="#8b5cf6" strokeWidth="0.5" strokeOpacity="0.4"/>
          <rect x="10" y="12" width="70" height="5" rx="2" fill="#c4b5fd" fillOpacity="0.5"/>
          <rect x="10" y="24" width="90" height="4" rx="2" fill="#ddd6fe" fillOpacity="0.3"/>
        </g>
        
        <defs>
          <linearGradient id="dashTrendGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#10b981"/>
            <stop offset="100%" stopColor="#8b5cf6"/>
          </linearGradient>
        </defs>
      </svg>
    </div>
  );
}

// Step Visuals: Abstract illustrations for getting started steps

// Step 1: Budget Setup Visual
function BudgetSetupVisual() {
  return (
    <div className="relative w-full h-48 rounded-xl overflow-hidden bg-gradient-to-br from-indigo-950/60 via-slate-900 to-violet-950/40">
      <div className="absolute top-1/2 left-1/3 w-32 h-32 bg-indigo-500/20 rounded-full blur-3xl" />
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 200" fill="none">
        {/* Envelope grid setup */}
        <g transform="translate(40, 30)">
          {/* First envelope - being created */}
          <rect width="90" height="60" rx="8" fill="#1e293b" stroke="#818cf8" strokeWidth="1.5" strokeDasharray="4 2"/>
          <rect x="10" y="12" width="50" height="5" rx="2" fill="#a78bfa" fillOpacity="0.6"/>
          <rect x="10" y="22" width="30" height="4" rx="2" fill="#c4b5fd" fillOpacity="0.4"/>
          <circle cx="70" cy="40" r="12" fill="none" stroke="#818cf8" strokeWidth="1" strokeOpacity="0.5"/>
          <line x1="64" y1="40" x2="76" y2="40" stroke="#818cf8" strokeWidth="1.5"/>
          <line x1="70" y1="34" x2="70" y2="46" stroke="#818cf8" strokeWidth="1.5"/>
        </g>
        
        {/* Second envelope */}
        <g transform="translate(150, 30)">
          <rect width="90" height="60" rx="8" fill="#1e293b" stroke="#10b981" strokeWidth="1"/>
          <rect x="10" y="12" width="60" height="5" rx="2" fill="#6ee7b7" fillOpacity="0.6"/>
          <rect x="10" y="22" width="45" height="4" rx="2" fill="#a7f3d0" fillOpacity="0.4"/>
          <rect x="10" y="38" width="70" height="12" rx="3" fill="#10b981" fillOpacity="0.3"/>
        </g>
        
        {/* Third envelope */}
        <g transform="translate(260, 30)">
          <rect width="90" height="60" rx="8" fill="#1e293b" stroke="#f59e0b" strokeWidth="1"/>
          <rect x="10" y="12" width="55" height="5" rx="2" fill="#fcd34d" fillOpacity="0.6"/>
          <rect x="10" y="22" width="40" height="4" rx="2" fill="#fde68a" fillOpacity="0.4"/>
          <rect x="10" y="38" width="70" height="12" rx="3" fill="#f59e0b" fillOpacity="0.3"/>
        </g>
        
        {/* More envelopes below */}
        <g transform="translate(80, 110)">
          <rect width="90" height="60" rx="8" fill="#1e293b" fillOpacity="0.6" stroke="#818cf8" strokeWidth="0.5" strokeOpacity="0.4"/>
          <rect x="10" y="15" width="50" height="4" rx="2" fill="#a78bfa" fillOpacity="0.3"/>
          <rect x="10" y="30" width="65" height="10" rx="2" fill="#6366f1" fillOpacity="0.2"/>
        </g>
        <g transform="translate(190, 110)">
          <rect width="90" height="60" rx="8" fill="#1e293b" fillOpacity="0.6" stroke="#818cf8" strokeWidth="0.5" strokeOpacity="0.4"/>
          <rect x="10" y="15" width="45" height="4" rx="2" fill="#a78bfa" fillOpacity="0.3"/>
          <rect x="10" y="30" width="55" height="10" rx="2" fill="#6366f1" fillOpacity="0.2"/>
        </g>
        
        {/* Setup arrow/flow */}
        <path d="M20 100 Q30 80 40 100" stroke="#818cf8" strokeWidth="1" fill="none" strokeOpacity="0.5"/>
        <circle cx="20" cy="100" r="8" fill="#4f46e5" fillOpacity="0.3" stroke="#818cf8" strokeWidth="1"/>
      </svg>
    </div>
  );
}

// Step 2: Add Income Visual
function AddIncomeVisual() {
  return (
    <div className="relative w-full h-48 rounded-xl overflow-hidden bg-gradient-to-br from-emerald-950/60 via-slate-900 to-teal-950/40">
      <div className="absolute top-1/2 right-1/3 w-32 h-32 bg-emerald-500/20 rounded-full blur-3xl" />
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 200" fill="none">
        {/* Income form abstraction */}
        <rect x="80" y="30" width="240" height="140" rx="12" fill="#1e293b" fillOpacity="0.8" stroke="#10b981" strokeWidth="1"/>
        
        {/* Form header */}
        <rect x="100" y="50" width="80" height="8" rx="4" fill="#6ee7b7" fillOpacity="0.7"/>
        
        {/* Amount input - highlighted */}
        <rect x="100" y="75" width="200" height="35" rx="6" fill="#064e3b" fillOpacity="0.4" stroke="#10b981" strokeWidth="1"/>
        <text x="120" y="98" fill="#6ee7b7" fontSize="18" fontFamily="system-ui" fontWeight="bold">+ $</text>
        <rect x="165" y="85" width="80" height="12" rx="2" fill="#6ee7b7" fillOpacity="0.5"/>
        
        {/* Category dropdown */}
        <rect x="100" y="125" width="200" height="30" rx="6" fill="#1e293b" fillOpacity="0.6" stroke="#6ee7b7" strokeWidth="0.5" strokeOpacity="0.5"/>
        <rect x="115" y="137" width="60" height="6" rx="2" fill="#a7f3d0" fillOpacity="0.4"/>
        
        {/* Floating coins/money symbols */}
        <circle cx="50" cy="60" r="15" fill="#10b981" fillOpacity="0.2" stroke="#6ee7b7" strokeWidth="1"/>
        <text x="45" y="65" fill="#6ee7b7" fontSize="14">$</text>
        <circle cx="350" cy="100" r="12" fill="#10b981" fillOpacity="0.15" stroke="#6ee7b7" strokeWidth="0.5"/>
        <circle cx="365" cy="140" r="8" fill="#10b981" fillOpacity="0.1" stroke="#6ee7b7" strokeWidth="0.5"/>
      </svg>
    </div>
  );
}

// Step 3: Record Expense Visual
function RecordExpenseVisual() {
  return (
    <div className="relative w-full h-48 rounded-xl overflow-hidden bg-gradient-to-br from-rose-950/60 via-slate-900 to-orange-950/40">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 w-32 h-32 bg-rose-500/15 rounded-full blur-3xl" />
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 200" fill="none">
        {/* Expense transaction visualization */}
        <rect x="60" y="40" width="120" height="120" rx="12" fill="#1e293b" fillOpacity="0.8" stroke="#ef4444" strokeWidth="1"/>
        
        {/* Expense label */}
        <rect x="80" y="60" width="50" height="6" rx="3" fill="#fca5a5" fillOpacity="0.6"/>
        
        {/* Amount */}
        <text x="80" y="95" fill="#f87171" fontSize="16" fontFamily="system-ui" fontWeight="bold">- $</text>
        <rect x="110" y="82" width="50" height="12" rx="2" fill="#f87171" fillOpacity="0.5"/>
        
        {/* Category */}
        <rect x="80" y="110" width="80" height="25" rx="5" fill="#7f1d1d" fillOpacity="0.3" stroke="#ef4444" strokeWidth="0.5" strokeOpacity="0.5"/>
        <rect x="92" y="120" width="40" height="5" rx="2" fill="#fca5a5" fillOpacity="0.4"/>
        
        {/* Arrow pointing to envelope */}
        <path d="M190 100 L230 100" stroke="url(#expenseFlowGrad)" strokeWidth="2" strokeLinecap="round"/>
        <path d="M225 95 L235 100 L225 105" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        
        {/* Target envelope */}
        <rect x="250" y="50" width="100" height="100" rx="10" fill="#1e293b" fillOpacity="0.7" stroke="#818cf8" strokeWidth="1"/>
        <rect x="265" y="70" width="60" height="6" rx="3" fill="#a78bfa" fillOpacity="0.6"/>
        <rect x="265" y="85" width="70" height="20" rx="4" fill="#4c1d95" fillOpacity="0.3"/>
        <rect x="265" y="115" width="70" height="8" rx="4" fill="#1e293b"/>
        <rect x="265" y="115" width="45" height="8" rx="4" fill="#ef4444" fillOpacity="0.6"/>
        
        <defs>
          <linearGradient id="expenseFlowGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#ef4444"/>
            <stop offset="100%" stopColor="#818cf8"/>
          </linearGradient>
        </defs>
      </svg>
    </div>
  );
}

// Step 4: Shopping List Visual
function ShoppingListSetupVisual() {
  return (
    <div className="relative w-full h-48 rounded-xl overflow-hidden bg-gradient-to-br from-amber-950/60 via-slate-900 to-orange-950/40">
      <div className="absolute top-1/3 right-1/4 w-28 h-28 bg-amber-500/15 rounded-full blur-2xl" />
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 200" fill="none">
        {/* Main list */}
        <rect x="80" y="25" width="160" height="150" rx="10" fill="#1e293b" fillOpacity="0.8" stroke="#f59e0b" strokeWidth="1"/>
        
        {/* List header */}
        <rect x="100" y="40" width="80" height="7" rx="3" fill="#fcd34d" fillOpacity="0.6"/>
        
        {/* List items */}
        <g transform="translate(100, 60)">
          <circle cx="6" cy="6" r="5" fill="none" stroke="#f59e0b" strokeWidth="1.5"/>
          <rect x="20" y="2" width="90" height="6" rx="2" fill="#fde68a" fillOpacity="0.4"/>
        </g>
        <g transform="translate(100, 82)">
          <circle cx="6" cy="6" r="5" fill="#f59e0b" fillOpacity="0.6"/>
          <path d="M3 6 L5 8 L9 4" stroke="#fef3c7" strokeWidth="1.5" fill="none"/>
          <rect x="20" y="2" width="70" height="6" rx="2" fill="#fde68a" fillOpacity="0.3"/>
        </g>
        <g transform="translate(100, 104)">
          <circle cx="6" cy="6" r="5" fill="none" stroke="#f59e0b" strokeWidth="1.5"/>
          <rect x="20" y="2" width="85" height="6" rx="2" fill="#fde68a" fillOpacity="0.4"/>
        </g>
        <g transform="translate(100, 126)">
          <circle cx="6" cy="6" r="5" fill="none" stroke="#f59e0b" strokeWidth="1.5"/>
          <rect x="20" y="2" width="60" height="6" rx="2" fill="#fde68a" fillOpacity="0.4"/>
        </g>
        
        {/* Share icon/concept */}
        <g transform="translate(260, 50)">
          <circle r="25" fill="#1e293b" stroke="#f59e0b" strokeWidth="0.5" strokeOpacity="0.5"/>
          <circle cx="0" cy="-10" r="4" fill="#f59e0b" fillOpacity="0.6"/>
          <circle cx="-10" cy="8" r="4" fill="#f59e0b" fillOpacity="0.4"/>
          <circle cx="10" cy="8" r="4" fill="#f59e0b" fillOpacity="0.4"/>
          <line x1="0" y1="-7" x2="-8" y2="5" stroke="#f59e0b" strokeWidth="1" strokeOpacity="0.5"/>
          <line x1="0" y1="-7" x2="8" y2="5" stroke="#f59e0b" strokeWidth="1" strokeOpacity="0.5"/>
        </g>
        
        {/* Voice mic hint */}
        <g transform="translate(280, 120)">
          <rect x="-10" y="-20" width="20" height="30" rx="8" fill="#1e293b" stroke="#f59e0b" strokeWidth="1"/>
          <path d="M-15 5 Q-15 20 0 20 Q15 20 15 5" fill="none" stroke="#f59e0b" strokeWidth="1"/>
          <line x1="0" y1="20" x2="0" y2="30" stroke="#f59e0b" strokeWidth="1"/>
          <line x1="-8" y1="30" x2="8" y2="30" stroke="#f59e0b" strokeWidth="1"/>
        </g>
      </svg>
    </div>
  );
}

// Step 5: FinTasks Setup Visual
function FinTasksSetupVisual() {
  return (
    <div className="relative w-full h-48 rounded-xl overflow-hidden bg-gradient-to-br from-blue-950/60 via-slate-900 to-cyan-950/40">
      <div className="absolute bottom-1/3 left-1/3 w-28 h-28 bg-blue-500/15 rounded-full blur-2xl" />
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 200" fill="none">
        {/* Task cards grid */}
        <g transform="translate(40, 35)">
          <rect width="100" height="65" rx="8" fill="#1e293b" fillOpacity="0.8" stroke="#3b82f6" strokeWidth="1"/>
          <rect x="12" y="12" width="60" height="6" rx="3" fill="#93c5fd" fillOpacity="0.6"/>
          <rect x="12" y="25" width="75" height="5" rx="2" fill="#bfdbfe" fillOpacity="0.3"/>
          <rect x="12" y="45" width="40" height="12" rx="4" fill="#1d4ed8" fillOpacity="0.4"/>
          <circle cx="80" cy="50" r="10" fill="none" stroke="#3b82f6" strokeWidth="1.5"/>
        </g>
        
        <g transform="translate(155, 35)">
          <rect width="100" height="65" rx="8" fill="#1e293b" fillOpacity="0.8" stroke="#10b981" strokeWidth="1"/>
          <rect x="12" y="12" width="55" height="6" rx="3" fill="#6ee7b7" fillOpacity="0.6"/>
          <rect x="12" y="25" width="65" height="5" rx="2" fill="#a7f3d0" fillOpacity="0.3"/>
          <rect x="12" y="45" width="45" height="12" rx="4" fill="#065f46" fillOpacity="0.4"/>
          <circle cx="80" cy="50" r="10" fill="#10b981" fillOpacity="0.3"/>
          <path d="M75 50 L78 53 L85 46" stroke="#6ee7b7" strokeWidth="2" fill="none"/>
        </g>
        
        <g transform="translate(270, 35)">
          <rect width="100" height="65" rx="8" fill="#1e293b" fillOpacity="0.8" stroke="#f59e0b" strokeWidth="1"/>
          <rect x="12" y="12" width="50" height="6" rx="3" fill="#fcd34d" fillOpacity="0.6"/>
          <rect x="12" y="25" width="70" height="5" rx="2" fill="#fde68a" fillOpacity="0.3"/>
          <rect x="12" y="45" width="35" height="12" rx="4" fill="#92400e" fillOpacity="0.4"/>
          <circle cx="80" cy="50" r="10" fill="none" stroke="#f59e0b" strokeWidth="1.5"/>
        </g>
        
        {/* Lower row - more tasks */}
        <g transform="translate(80, 115)">
          <rect width="100" height="55" rx="8" fill="#1e293b" fillOpacity="0.6" stroke="#3b82f6" strokeWidth="0.5" strokeOpacity="0.5"/>
          <rect x="12" y="12" width="50" height="5" rx="2" fill="#93c5fd" fillOpacity="0.4"/>
          <rect x="12" y="25" width="65" height="4" rx="2" fill="#bfdbfe" fillOpacity="0.2"/>
        </g>
        <g transform="translate(200, 115)">
          <rect width="100" height="55" rx="8" fill="#1e293b" fillOpacity="0.6" stroke="#3b82f6" strokeWidth="0.5" strokeOpacity="0.5"/>
          <rect x="12" y="12" width="55" height="5" rx="2" fill="#93c5fd" fillOpacity="0.4"/>
          <rect x="12" y="25" width="60" height="4" rx="2" fill="#bfdbfe" fillOpacity="0.2"/>
        </g>
      </svg>
    </div>
  );
}

// Step 6: Goals Setup Visual
function GoalsSetupVisual() {
  return (
    <div className="relative w-full h-48 rounded-xl overflow-hidden bg-gradient-to-br from-violet-950/60 via-slate-900 to-purple-950/40">
      <div className="absolute top-1/2 right-1/3 w-28 h-28 bg-purple-500/15 rounded-full blur-2xl" />
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 200" fill="none">
        {/* Goal creation form */}
        <rect x="60" y="30" width="180" height="140" rx="12" fill="#1e293b" fillOpacity="0.8" stroke="#8b5cf6" strokeWidth="1"/>
        
        {/* Goal name */}
        <rect x="80" y="50" width="100" height="8" rx="4" fill="#c4b5fd" fillOpacity="0.6"/>
        
        {/* Target amount */}
        <rect x="80" y="75" width="140" height="30" rx="6" fill="#4c1d95" fillOpacity="0.3" stroke="#8b5cf6" strokeWidth="0.5"/>
        <text x="95" y="95" fill="#a78bfa" fontSize="12" fontFamily="system-ui">Target: $</text>
        <rect x="155" y="85" width="50" height="8" rx="2" fill="#a78bfa" fillOpacity="0.5"/>
        
        {/* Progress bar setup */}
        <rect x="80" y="120" width="140" height="10" rx="5" fill="#1e293b"/>
        <rect x="80" y="120" width="60" height="10" rx="5" fill="url(#goalSetupGrad)"/>
        
        {/* Savings target visual */}
        <g transform="translate(280, 60)">
          <circle r="50" fill="#1e293b" fillOpacity="0.6" stroke="#8b5cf6" strokeWidth="1"/>
          <circle r="40" fill="none" stroke="#4c1d95" strokeWidth="8"/>
          <circle r="40" fill="none" stroke="url(#goalCircleGrad)" strokeWidth="8" strokeDasharray="125.6 251.2" transform="rotate(-90)"/>
          <text y="5" textAnchor="middle" fill="#c4b5fd" fontSize="14" fontFamily="system-ui" fontWeight="bold">50%</text>
        </g>
        
        <defs>
          <linearGradient id="goalSetupGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#8b5cf6"/>
            <stop offset="100%" stopColor="#a78bfa"/>
          </linearGradient>
          <linearGradient id="goalCircleGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#8b5cf6"/>
            <stop offset="100%" stopColor="#c4b5fd"/>
          </linearGradient>
        </defs>
      </svg>
    </div>
  );
}

// Feature Card Component - With abstract visual support
function FeatureCard({ icon: Icon, title, description, color, visualType, delay = 0 }) {
  const renderVisual = () => {
    switch (visualType) {
      case 'budget':
        return <BudgetVisual />;
      case 'transactions':
        return <TransactionsVisual />;
      case 'shopping':
        return <ShoppingVisual />;
      case 'tasks':
        return <TasksVisual />;
      case 'goals':
        return <GoalsVisual />;
      case 'help':
        return <HelpVisual />;
      default:
        return (
          <div className="h-40 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-slate-700/50 dark:to-slate-800/50 flex items-center justify-center">
            <Icon size={32} className="text-muted-foreground/30" />
          </div>
        );
    }
  };

  return (
    <motion.div
      variants={fadeInUp}
      className="group relative p-6 rounded-2xl bg-white dark:bg-slate-800/80 border border-gray-100 dark:border-slate-700 hover:border-indigo-200 dark:hover:border-indigo-600 transition-all duration-300 hover:shadow-lg hover:shadow-indigo-500/10"
    >
      <div className={`inline-flex p-3 rounded-xl mb-4 ${color}`}>
        <Icon size={24} className="text-white" />
      </div>
      <h3 className="text-lg font-semibold text-foreground mb-2">{title}</h3>
      <p className="text-muted-foreground text-sm leading-relaxed">{description}</p>
      
      {/* Product visual - Abstract illustration */}
      <div className="mt-4 rounded-xl overflow-hidden border border-gray-200 dark:border-slate-600 shadow-sm">
        {renderVisual()}
      </div>
    </motion.div>
  );
}

// Step Card Component for Getting Started - With abstract visual support
function StepCard({ number, title, description, cta, ctaLink, visualType, children }) {
  const renderVisual = () => {
    switch (visualType) {
      case 'budget-setup':
        return <BudgetSetupVisual />;
      case 'add-income':
        return <AddIncomeVisual />;
      case 'record-expense':
        return <RecordExpenseVisual />;
      case 'shopping-list':
        return <ShoppingListSetupVisual />;
      case 'fintasks':
        return <FinTasksSetupVisual />;
      case 'goals':
        return <GoalsSetupVisual />;
      default:
        return (
          <div className="h-48 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-slate-700/50 dark:to-slate-800/50 flex items-center justify-center">
            <span className="text-6xl">📋</span>
          </div>
        );
    }
  };

  return (
    <motion.div
      variants={fadeInUp}
      className="relative"
    >
      <div className="flex gap-4">
        {/* Step number indicator */}
        <div className="flex-shrink-0">
          <div className="w-10 h-10 rounded-full bg-indigo-500 text-white flex items-center justify-center font-bold text-lg">
            {number}
          </div>
          {/* Connecting line */}
          <div className="hidden md:block w-0.5 h-full bg-gradient-to-b from-indigo-500 to-transparent mx-auto mt-2" />
        </div>
        
        {/* Content */}
        <div className="flex-1 pb-8">
          <h3 className="text-xl font-semibold text-foreground mb-2">{title}</h3>
          <p className="text-muted-foreground mb-4 leading-relaxed">{description}</p>
          {children}
          {cta && (
            <Link to={ctaLink || '/signin'}>
              <Button variant="outline" size="sm" className="mt-4 group">
                {cta}
                <ChevronRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          )}
          
          {/* Step visual - Abstract illustration */}
          <div className="mt-4 rounded-xl overflow-hidden border border-gray-200 dark:border-slate-600 shadow-sm">
            {renderVisual()}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// Deep Dive Card - With abstract visual support
function DeepDiveCard({ icon: Icon, title, tagline, color, visualType }) {
  const renderVisual = () => {
    switch (visualType) {
      case 'budget':
        return <BudgetVisual />;
      case 'transactions':
        return <TransactionsVisual />;
      case 'shopping':
        return <ShoppingVisual />;
      case 'tasks':
        return <TasksVisual />;
      case 'goals':
        return <GoalsVisual />;
      case 'help':
        return <HelpVisual />;
      default:
        return (
          <div className="h-32 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-slate-700/50 dark:to-slate-800/50 flex items-center justify-center">
            <Icon size={24} className="text-muted-foreground/30" />
          </div>
        );
    }
  };

  return (
    <motion.div
      variants={fadeInScale}
      className="p-6 rounded-2xl bg-white dark:bg-slate-800/80 border border-gray-100 dark:border-slate-700"
    >
      <div className={`inline-flex p-3 rounded-xl mb-3 ${color}`}>
        <Icon size={20} className="text-white" />
      </div>
      <h4 className="font-semibold text-foreground mb-1">{title}</h4>
      <p className="text-sm text-muted-foreground">{tagline}</p>
      
      {/* Visual - Abstract illustration */}
      <div className="mt-4 rounded-lg overflow-hidden border border-gray-200 dark:border-slate-600 shadow-sm">
        {renderVisual()}
      </div>
    </motion.div>
  );
}

export default function WelcomePage() {
  const navigate = useNavigate();
  const { currentUser, loading } = useAuth();

  // Redirect authenticated users to dashboard
  useEffect(() => {
    if (!loading && currentUser) {
      navigate('/dashboard', { replace: true });
    }
  }, [currentUser, loading, navigate]);

  // Show content immediately - don't block on auth loading for welcome page
  // The redirect above will handle authenticated users once auth resolves

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      {/* Navigation Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-gray-100 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <FinListLogo className="w-9 h-9" />
              <span className="text-xl font-bold text-foreground">FinList</span>
            </div>
            
            {/* CTA Buttons */}
            <div className="flex items-center gap-3">
              <Link to="/signin">
                <Button variant="ghost" size="sm">Sign In</Button>
              </Link>
              <Link to="/signup">
                <Button size="sm" className="bg-indigo-500 hover:bg-indigo-600">
                  Get Started Free
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main className="pt-16">
        {/* ═══════════════════════════════════════════════════════════════════
            SECTION 1: HERO
        ═══════════════════════════════════════════════════════════════════ */}
        <section className="relative py-20 lg:py-32 overflow-hidden">
          {/* Background gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 via-white to-purple-50 dark:from-slate-900 dark:via-slate-900 dark:to-indigo-950" />
          <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-indigo-100/50 dark:from-indigo-900/20 to-transparent" />
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left: Text */}
              <motion.div
                initial="hidden"
                animate="visible"
                variants={staggerContainer}
                className="text-center lg:text-left"
              >
                <motion.div variants={fadeInUp} className="mb-4">
                  <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400 text-sm font-medium">
                    <Sparkles size={14} />
                    Everything about your money — finally connected
                  </span>
                </motion.div>
                
                <motion.h1 
                  variants={fadeInUp}
                  className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-tight mb-6"
                >
                  All your money, plans, tasks, and goals —{' '}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 to-purple-500">
                    working together
                  </span>
                </motion.h1>
                
                <motion.p 
                  variants={fadeInUp}
                  className="text-lg text-muted-foreground mb-8 max-w-xl mx-auto lg:mx-0"
                >
                  FinList helps you budget smarter, track spending clearly, plan shopping easily, 
                  manage financial tasks, and reach your goals — all from one simple system.
                </motion.p>
                
                <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                  <Link to="/signup">
                    <Button size="lg" className="bg-indigo-500 hover:bg-indigo-600 w-full sm:w-auto">
                      Get Started Free
                      <ArrowRight size={18} className="ml-2" />
                    </Button>
                  </Link>
                  <Button variant="outline" size="lg" className="w-full sm:w-auto">
                    <Play size={18} className="mr-2" />
                    See How FinList Works
                  </Button>
                </motion.div>
              </motion.div>
              
              {/* Right: Hero Visual - Abstract Illustration */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="hidden lg:block"
              >
                <div className="relative">
                  <div className="rounded-2xl overflow-hidden border border-gray-200 dark:border-slate-700 shadow-2xl shadow-indigo-500/10">
                    <HeroVisual />
                  </div>
                  {/* Floating elements */}
                  <div className="absolute -top-4 -right-4 w-20 h-20 bg-gradient-to-br from-emerald-400 to-emerald-500 rounded-xl shadow-lg flex items-center justify-center">
                    <TrendingUp size={32} className="text-white" />
                  </div>
                  <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl shadow-lg flex items-center justify-center">
                    <Target size={24} className="text-white" />
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════════
            SECTION 2: TRUST STRIP
        ═══════════════════════════════════════════════════════════════════ */}
        <section className="py-8 border-y border-gray-100 dark:border-slate-800 bg-gray-50/50 dark:bg-slate-800/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-12 text-center"
            >
              <div className="flex items-center gap-2 text-muted-foreground">
                <Shield size={18} className="text-emerald-500" />
                <span className="text-sm font-medium">No banks required</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Globe size={18} className="text-blue-500" />
                <span className="text-sm font-medium">Works worldwide</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Sparkles size={18} className="text-purple-500" />
                <span className="text-sm font-medium">Built for clarity, not complexity</span>
              </div>
            </motion.div>
            
            {/* Feature icons row */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="flex items-center justify-center gap-8 mt-6"
            >
              {[
                { icon: Wallet, label: 'Budget' },
                { icon: BarChart3, label: 'Transactions' },
                { icon: ShoppingCart, label: 'Shopping' },
                { icon: CheckSquare, label: 'Tasks' },
                { icon: Target, label: 'Goals' }
              ].map((item, i) => (
                <div key={i} className="flex flex-col items-center gap-1">
                  <div className="p-2 rounded-lg bg-white dark:bg-slate-700 border border-gray-100 dark:border-slate-600">
                    <item.icon size={20} className="text-indigo-500" />
                  </div>
                  <span className="text-xs text-muted-foreground hidden sm:block">{item.label}</span>
                </div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════════
            SECTION 3: WHAT IS FINLIST? (Feature Grid)
        ═══════════════════════════════════════════════════════════════════ */}
        <section className="py-20 lg:py-28">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
              className="text-center mb-16"
            >
              <motion.p variants={fadeInUp} className="text-sm font-semibold text-indigo-500 uppercase tracking-wide mb-2">
                What is FinList?
              </motion.p>
              <motion.h2 variants={fadeInUp} className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
                One connected system — not separate tools
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-lg text-muted-foreground max-w-2xl mx-auto">
                FinList is designed so every part of your financial life talks to each other.
                No duplicate work. No confusion. No guessing where your money went.
              </motion.p>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              variants={staggerContainer}
              className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              <FeatureCard
                icon={Wallet}
                title="Budget — The Engine"
                description="Control how your money is planned and spent using budget envelopes."
                color="bg-gradient-to-br from-indigo-500 to-indigo-600"
                visualType="budget"
              />
              <FeatureCard
                icon={BarChart3}
                title="Transactions Hub — The Control Center"
                description="Record all income, expenses, transfers, and refunds in one place."
                color="bg-gradient-to-br from-emerald-500 to-emerald-600"
                visualType="transactions"
              />
              <FeatureCard
                icon={ShoppingCart}
                title="Shopping List — Plan & Share"
                description="Create clean, shareable shopping lists. Add items by typing or voice."
                color="bg-gradient-to-br from-amber-500 to-orange-500"
                visualType="shopping"
              />
              <FeatureCard
                icon={CheckSquare}
                title="FinTasks — What Needs Action"
                description="Track financial to-dos, reminders, and tasks linked to your money decisions."
                color="bg-gradient-to-br from-blue-500 to-blue-600"
                visualType="tasks"
              />
              <FeatureCard
                icon={Target}
                title="Savings & Goals — Your Future"
                description="Set goals, track progress, and stay motivated."
                color="bg-gradient-to-br from-purple-500 to-purple-600"
                visualType="goals"
              />
              <FeatureCard
                icon={HelpCircle}
                title="Help & Getting Support"
                description="Find answers, explore guides, and get the support you need."
                color="bg-gradient-to-br from-cyan-500 to-teal-500"
                visualType="help"
              />
            </motion.div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════════
            SECTION 4: GETTING STARTED (Step-by-Step Guide)
        ═══════════════════════════════════════════════════════════════════ */}
        <section className="py-20 lg:py-28 bg-gray-50/50 dark:bg-slate-800/30">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
              className="text-center mb-16"
            >
              <motion.p variants={fadeInUp} className="text-sm font-semibold text-indigo-500 uppercase tracking-wide mb-2">
                Getting Started
              </motion.p>
              <motion.h2 variants={fadeInUp} className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
                Getting Started with FinList
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-lg text-muted-foreground">
                To get the most out of FinList, follow these steps <strong>in order</strong>.
                This ensures everything works smoothly from day one.
              </motion.p>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              variants={staggerContainer}
              className="space-y-2"
            >
              <StepCard
                number="1"
                title="Set Up Your Budget Envelopes (Always First)"
                description="Before adding any transactions, go to the Budget page and create your budget categories (envelopes). Each envelope represents a spending category like Groceries, Transport, Entertainment, or Utilities. Allocate money to each envelope based on your income."
                cta="Go to Budget Page"
                ctaLink="/budget"
                visualType="budget-setup"
              />
              
              <StepCard
                number="2"
                title="Add Your Income"
                description="Tap the Transactions (+) button in the navigation bar. Select Income, enter the amount, choose a category (e.g. Salary), and save. This makes money available for your budget envelopes."
                cta="Add Income"
                ctaLink="/dashboard"
                visualType="add-income"
              />
              
              <StepCard
                number="3"
                title="Record Your Expenses"
                description="Use the same Transactions (+) button to record expenses. Select Expense, enter the amount, choose the correct Budget Envelope, and save. The amount is deducted directly from that envelope."
                cta="Log an Expense"
                ctaLink="/dashboard"
                visualType="record-expense"
              >
                <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                  <p className="text-sm text-amber-800 dark:text-amber-200">
                    <strong>Why this matters:</strong> Your budget stays accurate and your Dashboard updates instantly.
                  </p>
                </div>
              </StepCard>
              
              <StepCard
                number="4"
                title="Create Shopping Lists (Standalone & Shareable)"
                description="Create Shopping Lists to plan what you need to buy. Share lists with family or friends. Add items by typing or using your voice. Perfect for planning and collaboration."
                cta="Open Shopping Lists"
                ctaLink="/shopping-lists"
                visualType="shopping-list"
              >
                <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    <strong>🧠 Important:</strong> Shopping Lists <strong>do not record spending</strong>. 
                    When you actually spend money, log it in the Transactions Hub and link it to the correct budget envelope.
                  </p>
                </div>
              </StepCard>
            </motion.div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════════
            SECTION 5: DASHBOARD OVERVIEW
        ═══════════════════════════════════════════════════════════════════ */}
        <section className="py-20 lg:py-28">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={staggerContainer}
              >
                <motion.p variants={fadeInUp} className="text-sm font-semibold text-indigo-500 uppercase tracking-wide mb-2">
                  Dashboard
                </motion.p>
                <motion.h2 variants={fadeInUp} className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
                  Your financial overview — at a glance
                </motion.h2>
                <motion.p variants={fadeInUp} className="text-lg text-muted-foreground mb-6">
                  The Dashboard gives you a clear picture of:
                </motion.p>
                <motion.ul variants={fadeInUp} className="space-y-3 mb-6">
                  {[
                    'Safe-to-Spend',
                    'Net Balance',
                    'Spending by category',
                    'Trends and insights'
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-foreground">
                      <div className="w-2 h-2 rounded-full bg-indigo-500" />
                      {item}
                    </li>
                  ))}
                </motion.ul>
                <motion.div variants={fadeInUp} className="p-4 rounded-xl bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800">
                  <p className="text-sm text-indigo-800 dark:text-indigo-200">
                    <strong>🧭 Important rule:</strong> All money actions happen through the <strong>Transactions Hub</strong>. 
                    The Dashboard shows you the results.
                  </p>
                </motion.div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <div className="rounded-2xl overflow-hidden border border-gray-200 dark:border-slate-700 shadow-xl">
                  <DashboardVisual />
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════════
            SECTION 6: DEEP DIVES
        ═══════════════════════════════════════════════════════════════════ */}
        <section className="py-20 lg:py-28 bg-gray-50/50 dark:bg-slate-800/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
              className="text-center mb-12"
            >
              <motion.p variants={fadeInUp} className="text-sm font-semibold text-indigo-500 uppercase tracking-wide mb-2">
                Deep Dives
              </motion.p>
              <motion.h2 variants={fadeInUp} className="text-3xl sm:text-4xl font-bold text-foreground">
                How Each Service Works
              </motion.h2>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
              className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6"
            >
              <DeepDiveCard
                icon={ShoppingCart}
                title="Shopping List"
                tagline="Plan → Share → Shop. No prices, no stress — just clarity."
                color="bg-gradient-to-br from-amber-500 to-orange-500"
                visualType="shopping"
              />
              <DeepDiveCard
                icon={CheckSquare}
                title="FinTasks"
                tagline="Stay on top of what matters: reminders, actions, notifications."
                color="bg-gradient-to-br from-blue-500 to-blue-600"
                visualType="tasks"
              />
              <DeepDiveCard
                icon={Target}
                title="Savings & Goals"
                tagline="Set goals, track progress, celebrate milestones."
                color="bg-gradient-to-br from-purple-500 to-purple-600"
                visualType="goals"
              />
              <DeepDiveCard
                icon={HelpCircle}
                title="Help & Support"
                tagline="Guides, FAQs, and resources — we've got your back."
                color="bg-gradient-to-br from-cyan-500 to-teal-500"
                visualType="help"
              />
            </motion.div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════════
            SECTION 7: FINAL CTA
        ═══════════════════════════════════════════════════════════════════ */}
        <section className="py-20 lg:py-28 relative overflow-hidden">
          {/* Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-500 via-purple-500 to-indigo-600" />
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDM0djItSDI0di0yaDEyek0zNiAyNHYySDI0di0yaDEyeiIvPjwvZz48L2c+PC9zdmc+')] opacity-30" />
          
          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              <motion.div variants={fadeInUp} className="mb-6">
                <FinListLogo className="w-16 h-16 mx-auto" />
              </motion.div>
              <motion.h2 variants={fadeInUp} className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
                You're no longer guessing — you're in control.
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-lg text-white/80 mb-8 max-w-xl mx-auto">
                FinList gives you clarity, structure, and peace of mind — without complexity.
              </motion.p>
              <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/signup">
                  <Button size="lg" className="bg-white text-indigo-600 hover:bg-gray-100 w-full sm:w-auto">
                    🚀 Create Your Free FinList Account
                  </Button>
                </Link>
                <Button variant="outline" size="lg" className="border-white/30 text-white hover:bg-white/10 w-full sm:w-auto">
                  🔍 Explore How FinList Works
                </Button>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════════
            SECTION 8: FOOTER
        ═══════════════════════════════════════════════════════════════════ */}
        <footer className="py-12 border-t border-gray-100 dark:border-slate-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              {/* Logo */}
              <div className="flex items-center gap-3">
                <FinListLogo className="w-8 h-8" />
                <span className="font-semibold text-foreground">FinList</span>
              </div>
              
              {/* Links */}
              <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground">
                <Link to="/terms" className="hover:text-foreground transition-colors">Terms of Service</Link>
                <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy Policy</Link>
                <Link to="/cookies" className="hover:text-foreground transition-colors">Cookies</Link>
                <Link to="/help" className="hover:text-foreground transition-colors">Support</Link>
              </div>
              
              {/* Copyright */}
              <p className="text-sm text-muted-foreground">
                © {new Date().getFullYear()} FinList. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
}
